/**
 * The territory screen (platform design §17.10). The container reads the ids
 * from the route and the one tool answer; the view renders it.
 */

import type { ReactNode } from 'react';

import { Page } from '../../../chrome/page';
import { EmptyState, QueryStates } from '../../../chrome/state';
import type { CampaignDetail, CampaignScope } from '../../campaign/record/api/schemas';
import { useCampaignRecord } from '../../campaign/record/api/queries';
import { useBreakdown, useLocalities, useTerritory, type LocalityOrder } from '../api/queries';
import type { TerritorySelection } from '../api/territorySelection';
import { useWindowSelection, WindowSelect } from '../shared/WindowSelect';
import { ElectorateStat } from './ElectorateStat';
import { tt } from './messages';
import { RAIL_METRICS, type RailMetric } from './NeighbourhoodsCard';
import { TerritoryView } from './TerritoryView';

/** The tool's own key set is always present, so no answer is "empty": what it
 *  did not compute renders as an absence in the card that shows it. */
const NEVER_EMPTY = () => false;

const METRICS = ['composition', 'electorate'];

const ORDER: Record<RailMetric, LocalityOrder> = {
  reach: 'reach_desc',
  trend: 'reach_delta_desc',
  comments: 'comment_volume_desc',
};

/** The campaign's unit under its ancestors, country left out: 'Alagoas › Maceió'. */
function scopeLabel(scope: CampaignScope): string {
  const above = scope.ancestors.filter((ancestor) => ancestor.level !== 'country');
  return [...above.map((ancestor) => ancestor.name), scope.name].join(' › ');
}

/** Today in the campaign's own zone, as the ISO date the route reads (`sv-SE` formats as
 *  `YYYY-MM-DD`). */
function todayIn(timezone: string): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: timezone });
}

/** The locality is the campaign's own electoral scope, and the election the one its candidature
 *  was linked from in the TSE roster; both are read from its record and never picked. A URL's
 *  `?election=&asOf=` still overrides them. */
export function Territory(
  props: {
    campaignId: string;
    tenantId: string;
    tilesUrl: string;
  } & Pick<TerritorySelection, 'electionId' | 'asOf'>,
): ReactNode {
  const campaign = useCampaignRecord(props.campaignId);
  const scope = campaign.data?.scope ?? null;
  if (campaign.status === 'success' && scope !== null) {
    return (
      <TerritoryScreen
        {...props}
        scope={scope}
        electionId={props.electionId ?? linkedElection(campaign.data)}
        asOf={props.asOf ?? todayIn(campaign.data.timezone)}
      />
    );
  }
  return (
    <Page>
      <QueryStates query={campaign} isEmpty={NEVER_EMPTY} empty={null}>
        {() => <EmptyState title={tt('scope.none')} />}
      </QueryStates>
    </Page>
  );
}

function linkedElection(campaign: CampaignDetail): string | null {
  return campaign.candidate?.tse_election_id ?? null;
}

function TerritoryScreen(props: {
  campaignId: string;
  tenantId: string;
  tilesUrl: string;
  scope: CampaignScope;
  electionId: string | null;
  asOf: string;
}): ReactNode {
  const { campaignId, tenantId, tilesUrl, electionId, asOf, scope } = props;
  const {
    key: windowKey,
    window,
    onChange: setWindowKey,
    values,
    set,
  } = useWindowSelection(`territory.${campaignId}`, {
    dimension: { default: '' },
    metric: { default: 'reach', values: RAIL_METRICS },
  });
  const dimensionId = values.dimension === '' ? null : values.dimension;
  const setDimensionId = (next: string | null): void => set({ dimension: next ?? '' });
  const metric = values.metric as RailMetric;
  const setMetric = (next: RailMetric): void => set({ metric: next });
  const query = useTerritory(campaignId, tenantId, {
    scopeRef: scope.place_id,
    electionId,
    asOf,
    metrics: METRICS,
  });
  const breakdown = useBreakdown(campaignId, tenantId, {
    metric: 'views',
    dimensionId,
    window,
  });
  const localities = useLocalities(campaignId, tenantId, { window, order: ORDER[metric] });

  return (
    <Page>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p data-slot="locality" className="text-caption text-muted-foreground">
          {scopeLabel(scope)}
        </p>
        <WindowSelect value={windowKey} onChange={setWindowKey} />
      </div>
      <TerritoryView
        electorate={
          electionId === null ? (
            <ElectorateStat electorate={null} note={tt('election.none')} />
          ) : (
            <QueryStates query={query} isEmpty={NEVER_EMPTY} empty={null}>
              {(territory) => (
                <ElectorateStat
                  electorate={territory.electorate ?? null}
                  note={
                    (territory.electorate?.registered ?? null) === null
                      ? tt('electorate.unloaded')
                      : undefined
                  }
                />
              )}
            </QueryStates>
          )
        }
        tilesUrl={tilesUrl}
        focus={scope.locality_id}
        campaignId={campaignId}
        breakdown={breakdown}
        dimensionId={dimensionId}
        onDimensionChange={setDimensionId}
        localities={localities}
        metric={metric}
        onMetricChange={setMetric}
      />
    </Page>
  );
}
